import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { ActionMenu, WrapperBox } from "../Theme/GlobalStyles";
import { globalTheme } from "../Theme/Theme";
import { BloombergIcon, CoworkLogo, EduspaceIcon, MakerslabIcon, Users } from "../Theme/Icons";
import {
  MODULES,
  getModulesForRoles,
  selectAppModule,
  setSelectedModule,
} from "../../redux/appModule/appModuleSlice";
import { useNavigate } from "react-router-dom";

// Display metadata per module. Eduspace reuses the Users icon as a placeholder
// until a dedicated icon is added.
const MODULE_MENU = {
  [MODULES.COWORK]: {
    dropmenu: "CoWork",
    subMenu: "For meeting room bookings",
    icon: <CoworkLogo />,
    value: MODULES.COWORK,
  },
  [MODULES.MAKERSLAB]: {
    dropmenu: "Makers Lab",
    subMenu: "For Lab bookings",
    icon: <MakerslabIcon />,
    value: MODULES.MAKERSLAB,
  },
  [MODULES.EDUSPACE]: {
    dropmenu: "Eduspace",
    subMenu: "For classroom bookings",
    icon: <EduspaceIcon />,
    value: MODULES.EDUSPACE,
  },
  [MODULES.BLOOMBERG]: {
    dropmenu: "Bloomberg",
    subMenu: "For Bloomberg bookings",
    icon: <BloombergIcon />,
    value: MODULES.BLOOMBERG,
  },
};

const getRolesFromCookie = () => {
  const cookie = Cookies.get("roles");
  return cookie ? JSON.parse(cookie) : [];
};

/**
 * Which modules the current user can access, derived from their roles.
 * `canSwitch` is true only when more than one module is available — i.e. when a
 * toggle should be shown. Pure (no side effects), so it's safe to call from a
 * page that also renders <ModuleSwitcher /> (e.g. to gate an adjacent divider).
 */
export const useAvailableModules = () => {
  const availableModules = useMemo(() => getModulesForRoles(getRolesFromCookie()), []);
  return { availableModules, canSwitch: availableModules.length > 1 };
};

/**
 * Module toggle. Renders the switcher only when the user has access to more than
 * one module; otherwise it locks the app to the single accessible module and
 * renders nothing. Drop it in wherever the module toggle/ActionMenu is needed.
 */
const ModuleSwitcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedModule = useSelector(selectAppModule);
  const { availableModules, canSwitch } = useAvailableModules();

  // Keep the active module valid for this user's roles. Handles the
  // single-module case (e.g. only CoWork → CoWork) and any stale/invalid value
  // left over from localStorage or the hostname default. Runs even when this
  // component renders null, so a locked-in single module is always correct.
  useEffect(() => {
    if (availableModules.length > 0 && !availableModules.includes(selectedModule)) {
      dispatch(setSelectedModule(availableModules[0]));
      navigate('/user-dashboard')
    }
  }, [availableModules, selectedModule, dispatch]);

  const menuList = useMemo(
    () =>
      availableModules
        .map((mod) => MODULE_MENU[mod])
        .filter(Boolean)
        .map((item) => ({
          ...item,
          action: () => {
            // ActionMenu already refuses to fire `action` for the option that
            // is currently selected, but guard here too: this is the callback
            // that would navigate the user off whatever page they are on, and
            // re-selecting the active module should never move them.
            if (item.value === selectedModule) return;
            dispatch(setSelectedModule(item.value))
            navigate('/user-dashboard')
          },
        })),
    [availableModules, dispatch, navigate, selectedModule]
  );

  // Nothing to switch between — hide the toggle entirely.
  if (!canSwitch) return null;

  return (
    <WrapperBox
      background={globalTheme.colors.grey8}
      padding={"8px 12px"}
      borderRadius={50}
      borderColor={globalTheme.colors.grey6}
      Width={"168px"}
      maxHeight={"max-content"}
      className={"pointer"}
      overflow={"visible"}
    >
      <ActionMenu btnName="" selectedValue={selectedModule} menuList={menuList} />
    </WrapperBox>
  );
};

export default ModuleSwitcher;
